import { type IDBPDatabase, openDB } from "idb";

/**
 * A generic IndexedDB wrapper for storing key-value pairs.
 * Use the hook `useIndexedDB` to interact with this class if possible.
 */
export class BTIndexedDB<StoreType, KeyType> {
    private static DB_NAME = "BTNet";
    private static version = 1;
    private static stores = new Map<string, string | string[]>();

    private storeName: string;
    private dbPromise: Promise<IDBPDatabase>;

    constructor(storeName: string, keyPath: string | string[]) {
        this.storeName = storeName;

        if (!BTIndexedDB.stores.has(storeName)) {
            BTIndexedDB.stores.set(storeName, keyPath);
            BTIndexedDB.version += 1;
            this.dbPromise = openDB(BTIndexedDB.DB_NAME, BTIndexedDB.version, {
                upgrade: (db) => {
                    if (!db.objectStoreNames.contains(storeName)) {
                        db.createObjectStore(storeName, { keyPath });
                    }
                },
            });
        } else {
            this.dbPromise = openDB(BTIndexedDB.DB_NAME, BTIndexedDB.version);
        }
    }

    async get(key: KeyType): Promise<StoreType | null> {
        const db = await this.dbPromise;
        const result = await db.get(this.storeName, key as IDBValidKey);
        return result ?? null;
    }

    async set(key: KeyType, value: StoreType): Promise<void> {
        const db = await this.dbPromise;
        if (BTIndexedDB.stores.get(this.storeName)) {
            await db.put(this.storeName, value);
        } else {
            await db.put(this.storeName, value, key as IDBValidKey);
        }
    }

    async remove(key: KeyType): Promise<void> {
        const db = await this.dbPromise;
        await db.delete(this.storeName, key as IDBValidKey);
    }

    async getAll(): Promise<StoreType[]> {
        const db = await this.dbPromise;
        return await db.getAll(this.storeName);
    }

    async clear(): Promise<void> {
        const db = await this.dbPromise;
        await db.clear(this.storeName);
    }
}
